import { useState } from 'react'
import { PageContainer, ProTable, type ProColumns } from '@ant-design/pro-components'
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Alert, Button, Tag } from 'antd'
import { ChevronDown, ListChecks, Plus } from 'lucide-react'
import { z } from 'zod'
import type { AccountDisplay } from '@super-mega/server/contracts/profile'
import { TableRowActions } from '@/components/table-row-actions.tsx'
import { DEFAULT_PAGE_SEARCH as DEFAULT_QUERY } from '@/const/list.ts'
import { RANGE_PRESETS } from '@/const/range-presets.ts'
import { useMemberCreatorOptions } from '@/features/access/index.ts'
import { AuthorizedDropdown } from '@/features/authorization/authorized-dropdown.tsx'
import { Authorized } from '@/features/authorization/authorized.tsx'
import {
  ExpenseBusinessBatchCreateModal,
  ExpenseBusinessEditor,
  useExpenseBusinessListActions
} from '@/features/master-data/index.ts'
import { UserAvatarName } from '@/features/profile/index.ts'
import { usePageTableNavigation, type PageTableSearch } from '@/hooks/index.ts'
import { usePersistedListQuery } from '@/hooks/use-persisted-list-query.ts'
import { useTRPC } from '@/trpc/react.ts'
import { getErrorMessage } from '@/utils/error.ts'
import { createListQueryPersistence } from '@/utils/list-query-persistence.ts'
import {
  createdAtSearchShape,
  createdAtSortSearchShape,
  creatorMemberSearchShape,
  getCreatedAtSearch,
  getDateRangeFormValue,
  type DateRangeFormValue
} from '@/utils/list-query.ts'
import { proRemoteSearch } from '@/utils/pro-select-search.ts'

type ExpenseBusinessQuery = PageTableSearch & {
  id?: number
  creatorMemberId?: number
  createdFrom?: string
  createdTo?: string
  sortField?: 'createdAt'
  sortOrder?: 'asc' | 'desc'
  keyword?: string
  status?: 'enabled' | 'disabled'
}

type ExpenseBusinessSearchFormValues = Pick<ExpenseBusinessQuery, 'keyword' | 'status' | 'id' | 'creatorMemberId'> & {
  createdAtRange?: DateRangeFormValue
}

type ExpenseBusinessRow = {
  id: number
  billCount: number
  name: string
  color: string
  isEnabled: boolean
  note: string | null
  creator: AccountDisplay
  createdAt: Date
  updatedAt: Date
}

const expenseBusinessSearchSchema = z.object({
  ...createdAtSearchShape,
  ...createdAtSortSearchShape,
  ...creatorMemberSearchShape,
  current: z.coerce.number().int().min(1).catch(DEFAULT_QUERY.current),
  pageSize: z.coerce.number().int().min(1).max(100).catch(DEFAULT_QUERY.pageSize),
  id: z.coerce.number().int().positive().optional().catch(undefined),
  keyword: z.string().trim().max(255).optional().catch(undefined),
  status: z.enum(['enabled', 'disabled']).optional().catch(undefined)
})

const listQuery = createListQueryPersistence(
  'super-mega:master-data:expense-businesses:list-query',
  expenseBusinessSearchSchema
)

export const Route = createFileRoute('/_authenticated/master-data/expense-businesses')({
  beforeLoad: (options) => {
    listQuery.restore(options)
  },
  component: ExpenseBusinessesPage,
  head: () => ({ meta: [{ title: '支出业务管理 · Super Mega' }] }),
  validateSearch: listQuery.validateSearch
})

function createExpenseBusinessColumns({
  canManage,
  onChangeStatus,
  onEdit,
  onRemove
}: {
  canManage: boolean
  onChangeStatus: (expenseBusiness: ExpenseBusinessRow) => void
  onEdit: (expenseBusiness: ExpenseBusinessRow) => void
  onRemove: (expenseBusiness: ExpenseBusinessRow) => void
}): ProColumns<ExpenseBusinessRow>[] {
  return [
    {
      dataIndex: 'keyword',
      fieldProps: { allowClear: true, placeholder: '支出业务名称或 ID' },
      hideInTable: true,
      title: '关键词 / ID'
    },
    {
      dataIndex: 'id',
      copyable: true,
      renderText: (value?: number) => (value === undefined ? '—' : String(value)),
      valueType: 'digit',
      fieldProps: { min: 1, precision: 0 },
      title: 'ID',
      width: '7rem',
      search: false
    },
    {
      dataIndex: 'name',
      ellipsis: true,
      render: (_, expenseBusiness) => (
        <Tag className="max-w-full truncate" color={expenseBusiness.color} title={expenseBusiness.name}>
          {expenseBusiness.name}
        </Tag>
      ),
      search: false,
      title: '支出业务名称',
      width: '14rem'
    },
    { dataIndex: 'billCount', title: '支出账单数', search: false },
    {
      dataIndex: 'note',
      ellipsis: true,
      renderText: (value?: string) => value || '—',
      search: false,
      title: '备注',
      width: '16rem'
    },
    {
      dataIndex: 'status',
      hideInTable: true,
      title: '状态',
      valueEnum: {
        enabled: { text: '已启用' },
        disabled: { text: '已停用' }
      }
    },
    {
      dataIndex: 'isEnabled',
      render: (_, expenseBusiness) => (
        <Tag color={expenseBusiness.isEnabled ? 'success' : 'default'}>
          {expenseBusiness.isEnabled ? '已启用' : '已停用'}
        </Tag>
      ),
      search: false,
      title: '状态',
      width: '6.5rem'
    },
    {
      dataIndex: 'creator',
      render: (_, expenseBusiness) => (
        <UserAvatarName avatar={expenseBusiness.creator.avatar} name={expenseBusiness.creator.name} size={28} />
      ),
      search: false,
      title: '创建人',
      width: '10rem'
    },
    {
      dataIndex: 'createdAt',
      search: false,
      title: '创建时间',
      sorter: true,
      valueType: 'dateTime',
      width: '12rem'
    },
    {
      dataIndex: 'updatedAt',
      search: false,
      title: '最近更新',
      valueType: 'dateTime',
      width: '12rem'
    },
    {
      align: 'center',
      fixed: 'right',
      render: (_, expenseBusiness) =>
        canManage ? (
          <TableRowActions
            resource="expenseBusiness"
            actions={[
              { key: 'edit', label: '编辑', onClick: () => onEdit(expenseBusiness) },
              {
                danger: expenseBusiness.isEnabled,
                key: 'status',
                label: expenseBusiness.isEnabled ? '停用' : '启用',
                onClick: () => onChangeStatus(expenseBusiness)
              },
              { danger: true, key: 'delete', label: '删除', onClick: () => onRemove(expenseBusiness) }
            ]}
          />
        ) : (
          '—'
        ),
      search: false,
      title: '操作',
      valueType: 'option',
      width: '10rem'
    }
  ]
}

function ExpenseBusinessesPage() {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const { currentSession } = Route.useRouteContext()
  const navigate = Route.useNavigate()
  const { search: query, navigateSearch } = usePersistedListQuery({
    persistence: listQuery,
    raw: Route.useSearch(),
    accountId: currentSession.id,
    navigate
  })
  const [batchCreateOpen, setBatchCreateOpen] = useState(false)
  const [editing, setEditing] = useState<ExpenseBusinessRow | 'new'>()
  const canManage =
    currentSession.isSuperAdmin ||
    currentSession.permissions.some(
      (permission) => permission.startsWith('expenseBusiness.') && !permission.endsWith('.read')
    )
  const expenseBusinesses = useQuery({
    ...trpc.expenseBusiness.list.queryOptions(query),
    placeholderData: keepPreviousData
  })
  const closeEditor = () => setEditing(undefined)
  const invalidate = () =>
    Promise.all([
      queryClient.invalidateQueries(trpc.expenseBusiness.list.queryFilter()),
      queryClient.invalidateQueries(trpc.expenseBusiness.options.queryFilter()),
      queryClient.invalidateQueries(trpc.bill.directoryOptions.queryFilter({ kind: 'expenseBusiness' }))
    ])
  const expenseBusinessActions = useExpenseBusinessListActions({
    currentPage: query.current,
    goToPreviousPage: () => navigateSearch({ search: (previous) => ({ ...previous, current: previous.current - 1 }) }),
    invalidate,
    rows: expenseBusinesses.data?.data ?? []
  })
  const table = usePageTableNavigation<ExpenseBusinessQuery, ExpenseBusinessSearchFormValues>({
    defaultSearch: DEFAULT_QUERY,
    getFormInitialValues: (search) => ({
      keyword: search.keyword,
      status: search.status,
      id: search.id,
      creatorMemberId: search.creatorMemberId,
      createdAtRange: getDateRangeFormValue(search.createdFrom, search.createdTo)
    }),
    getSearchFromForm: (values) => ({
      id: values.id,
      keyword: values.keyword?.trim() || undefined,
      status: values.status,
      creatorMemberId: values.creatorMemberId,
      ...getCreatedAtSearch(values.createdAtRange)
    }),
    navigate: navigateSearch,
    onQueryChange: () => {
      closeEditor()
      expenseBusinessActions.clearSelection()
    },
    search: query,
    showTotal: (total) => `共 ${total} 个支出业务`
  })

  const creators = useMemberCreatorOptions('expenseBusiness.read', query.creatorMemberId)
  const columns = createExpenseBusinessColumns({
    canManage,
    onChangeStatus: expenseBusinessActions.confirmChangeStatus,
    onEdit: (expenseBusiness) => setEditing(expenseBusiness),
    onRemove: expenseBusinessActions.confirmRemoveExpenseBusiness
  })

  return (
    <PageContainer
      breadcrumbRender={false}
      content="维护工资、业绩、回扣、佣金、水电和租赁费用等支出事项，用于主体支出分类。"
      title="支出业务管理"
    >
      {expenseBusinesses.isError ? (
        <Alert
          action={
            <Button onClick={() => void expenseBusinesses.refetch()} size="small">
              重试
            </Button>
          }
          className="mb-4"
          showIcon
          title={getErrorMessage(expenseBusinesses.error, '支出业务列表加载失败')}
          type="error"
        />
      ) : null}
      <ProTable<ExpenseBusinessRow, ExpenseBusinessSearchFormValues>
        columns={[
          ...columns,
          {
            title: '创建人',
            dataIndex: 'creatorMemberId',
            hideInTable: true,
            valueType: 'select',
            fieldProps: {
              options: creators.options,
              loading: creators.isLoading,
              ...proRemoteSearch(creators.onSearch)
            }
          },
          {
            title: '创建时间',
            dataIndex: 'createdAtRange',
            hideInTable: true,
            fieldProps: { presets: RANGE_PRESETS },
            valueType: 'dateRange'
          }
        ]}
        onChange={
          /* eslint-disable max-params -- Ant Design Table 的回调签名由组件定义。 */
          (_, __, sorter, extra) => {
            if (extra.action !== 'sort') {
              return
            }
            const item = Array.isArray(sorter) ? sorter[0] : sorter
            void navigateSearch({
              search: {
                ...query,
                current: 1,
                sortField: 'createdAt',
                sortOrder: item.order === 'ascend' ? 'asc' : 'desc'
              }
            })
          }
          /* eslint-enable max-params */
        }
        columnsState={{ persistenceKey: 'master-data-expense-businesses:table', persistenceType: 'localStorage' }}
        dataSource={expenseBusinesses.data?.data ?? []}
        loading={{ delay: 150, spinning: expenseBusinesses.isFetching }}
        onReset={table.onReset}
        onSubmit={table.onSubmit}
        options={{ reload: () => void expenseBusinesses.refetch() }}
        pagination={{ ...table.pagination, total: expenseBusinesses.data?.total ?? 0 }}
        formRef={table.formRef}
        rowKey="id"
        rowSelection={
          canManage
            ? {
                onChange: (keys) => expenseBusinessActions.setSelectedExpenseBusinessIds(keys.map(Number)),
                selectedRowKeys: expenseBusinessActions.selectedExpenseBusinessIds
              }
            : undefined
        }
        scroll={{ x: 'max-content' }}
        form={{ initialValues: table.formInitialValues }}
        search={{ defaultCollapsed: false, labelWidth: 'auto' }}
        size="small"
        toolBarRender={() =>
          canManage
            ? [
                <AuthorizedDropdown
                  resource="expenseBusiness"
                  key="batch-actions"
                  menu={expenseBusinessActions.batchMenu}
                  placement="bottomRight"
                  trigger={['click']}
                >
                  <Button
                    disabled={expenseBusinessActions.selectedExpenseBusinessIds.length === 0}
                    icon={<ListChecks aria-hidden size={16} />}
                  >
                    批量操作 <ChevronDown aria-hidden size={14} />
                  </Button>
                </AuthorizedDropdown>,
                <Button key="batch-create" onClick={() => setBatchCreateOpen(true)}>
                  批量添加支出业务
                </Button>,
                <Authorized key="new" permissions={['expenseBusiness.create']}>
                  <Button
                    icon={<Plus aria-hidden size={16} />}
                    key="new"
                    onClick={() => setEditing('new')}
                    type="primary"
                  >
                    新建支出业务
                  </Button>
                </Authorized>
              ]
            : []
        }
      />

      {batchCreateOpen ? (
        <ExpenseBusinessBatchCreateModal onClose={() => setBatchCreateOpen(false)} onSaved={invalidate} />
      ) : null}
      {editing && (
        <ExpenseBusinessEditor
          key={editing === 'new' ? 'new' : editing.id}
          editing={editing}
          onClose={closeEditor}
          onSaved={async () => {
            closeEditor()
            await invalidate()
          }}
        />
      )}
    </PageContainer>
  )
}
